import React, { useEffect, useState } from 'react';
import {
  Activity,
  CalendarCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  Leaf,
  Lock,
  MessageCircle,
  Receipt,
  ShieldCheck,
  Stethoscope,
  Users,
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  COMPANION_APP_ABSOLUTE_SCREENSHOTS,
  COMPANION_APP_BANNER_PATH,
  COMPANION_APP_BANNER_URL,
  COMPANION_APP_CANONICAL_URL,
  COMPANION_APP_COMPLIANCE_DISCLOSURE,
  COMPANION_APP_FAQS,
  COMPANION_APP_ICON_URL,
  COMPANION_APP_META_DESCRIPTION,
  COMPANION_APP_NAME,
  COMPANION_APP_PAGE_TITLE,
  COMPANION_APP_SCREENSHOTS,
  COMPANION_APP_STORE_ID,
  COMPANION_APP_STORE_URL,
  COMPANION_PLAY_STORE_URL,
  SITE_URL,
} from '@/data/companionAppSeo';
import appStoreBadge from '@/assets/images/store-badges/app-store-badge.svg';
import googlePlayBadge from '@/assets/images/store-badges/google-play-badge.png';
import styles from './CompanionAppPage.module.css';

const createMeta = (attributes: Record<string, string>) => {
  const element = document.createElement('meta');
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  element.setAttribute('data-companion-seo', 'true');
  document.head.appendChild(element);
};

const createLink = (attributes: Record<string, string>) => {
  const element = document.createElement('link');
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
  element.setAttribute('data-companion-seo', 'true');
  document.head.appendChild(element);
};

const createJsonLd = (value: Record<string, unknown>) => {
  const element = document.createElement('script');
  element.type = 'application/ld+json';
  element.textContent = JSON.stringify(value);
  element.setAttribute('data-companion-seo', 'true');
  document.head.appendChild(element);
};

const useCompanionAppSeo = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.querySelectorAll('[data-companion-seo="true"]').forEach(element => element.remove());

    document.title = COMPANION_APP_PAGE_TITLE;
    createMeta({ name: 'description', content: COMPANION_APP_META_DESCRIPTION });
    createMeta({ property: 'og:title', content: COMPANION_APP_PAGE_TITLE });
    createMeta({ property: 'og:description', content: COMPANION_APP_META_DESCRIPTION });
    createMeta({ property: 'og:url', content: COMPANION_APP_CANONICAL_URL });
    createMeta({ property: 'og:type', content: 'website' });
    createMeta({ property: 'og:site_name', content: 'CalorieScience' });
    createMeta({ property: 'og:image', content: COMPANION_APP_BANNER_URL });
    createMeta({ property: 'og:image:width', content: '1024' });
    createMeta({ property: 'og:image:height', content: '500' });
    createMeta({ property: 'og:image:alt', content: `${COMPANION_APP_NAME} preview` });
    createMeta({ name: 'twitter:card', content: 'summary_large_image' });
    createMeta({ name: 'twitter:title', content: COMPANION_APP_PAGE_TITLE });
    createMeta({ name: 'twitter:description', content: COMPANION_APP_META_DESCRIPTION });
    createMeta({ name: 'twitter:image', content: COMPANION_APP_BANNER_URL });
    createLink({ rel: 'canonical', href: COMPANION_APP_CANONICAL_URL });

    // Apple Smart App Banner: only rendered once the App Store listing is live,
    // so we never ship a banner pointing at a placeholder or wrong app id.
    if (COMPANION_APP_STORE_ID) {
      createMeta({ name: 'apple-itunes-app', content: `app-id=${COMPANION_APP_STORE_ID}` });
    }

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: COMPANION_APP_NAME,
      description: COMPANION_APP_META_DESCRIPTION,
      applicationCategory: 'HealthApplication',
      applicationSubCategory: 'Health & Fitness',
      operatingSystem: 'iOS',
      url: COMPANION_APP_CANONICAL_URL,
      ...(COMPANION_APP_STORE_URL ? { installUrl: COMPANION_APP_STORE_URL, downloadUrl: COMPANION_APP_STORE_URL } : {}),
      image: COMPANION_APP_ICON_URL,
      screenshot: COMPANION_APP_ABSOLUTE_SCREENSHOTS.map(shot => ({
        '@type': 'ImageObject',
        url: shot.url,
        description: shot.alt,
      })),
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      publisher: {
        '@type': 'Organization',
        name: 'CalorieScience',
        url: SITE_URL,
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/images/menu-bar-logo.svg`,
        },
      },
    });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: COMPANION_APP_FAQS.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });

    createJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: COMPANION_APP_NAME, item: COMPANION_APP_CANONICAL_URL },
      ],
    });

    return () => {
      document.querySelectorAll('[data-companion-seo="true"]').forEach(element => element.remove());
      document.title = previousTitle;
    };
  }, []);
};

const StoreBadges: React.FC<{ id: string; className?: string }> = ({ id, className }) => {
  const isLive = Boolean(COMPANION_APP_STORE_URL) || Boolean(COMPANION_PLAY_STORE_URL);

  return (
    <div className={`${styles.storeBadgeGroup} ${className ?? ''}`}>
      {!isLive ? <span className={styles.storeBadgeComingSoon}>Coming soon on</span> : null}
      <div className={styles.storeBadgeRow}>
        {COMPANION_APP_STORE_URL ? (
          <a
            href={COMPANION_APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
            data-analytics-event="Landing CTA Clicked"
            data-analytics-id={`${id}_apple`}
            data-analytics-label="Download on the App Store"
            data-analytics-section="companion_app"
          >
            <img src={appStoreBadge} alt="Download on the App Store" className={styles.storeBadgeApple} />
          </a>
        ) : (
          <img src={appStoreBadge} alt="Download on the App Store" className={styles.storeBadgeApple} />
        )}
        {COMPANION_PLAY_STORE_URL ? (
          <a
            href={COMPANION_PLAY_STORE_URL}
            target="_blank"
            rel="noreferrer"
            data-analytics-event="Landing CTA Clicked"
            data-analytics-id={`${id}_google`}
            data-analytics-label="Get it on Google Play"
            data-analytics-section="companion_app"
          >
            <img src={googlePlayBadge} alt="Get it on Google Play" className={styles.storeBadgeGoogle} />
          </a>
        ) : (
          <div className={styles.storeBadgePending}>
            <img src={googlePlayBadge} alt="Google Play" className={styles.storeBadgeGoogle} />
            <span className={styles.storeBadgeComingSoon}>Android coming soon</span>
          </div>
        )}
      </div>
    </div>
  );
};

const WhatItIsIllustration: React.FC = () => {
  return (
    <div className={styles.illustration} aria-hidden="true">
      <svg viewBox="0 0 440 400" className={styles.illustrationSvg} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="wiiBlob" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#e9fbfb" />
            <stop offset="100%" stopColor="#cdeef8" />
          </linearGradient>
          <linearGradient id="wiiRing" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0c7489" />
            <stop offset="100%" stopColor="#2ec4b6" />
          </linearGradient>
        </defs>

        <path
          d="M225,18 C305,12 384,58 404,138 C424,218 396,302 322,344 C248,386 146,392 84,340 C22,288 8,206 30,134 C52,62 145,24 225,18 Z"
          fill="url(#wiiBlob)"
        />

        <path
          d="M140,110 C100,100 75,95 60,88M292,110 C330,100 355,95 372,88M140,300 C100,308 75,314 58,322M292,300 C330,308 355,314 376,322"
          fill="none"
          stroke="#9fd7de"
          strokeWidth="1.5"
          strokeDasharray="4 5"
        />

        <rect x="146" y="78" width="152" height="270" rx="26" fill="#093c4a" opacity="0.08" />
        <rect x="140" y="70" width="152" height="270" rx="26" fill="#ffffff" stroke="#e3f3f6" strokeWidth="1.5" />

        <circle cx="164" cy="96" r="6" fill="#0c7489" />
        <rect x="178" y="91" width="60" height="6" rx="3" fill="#d8e6ea" />
        <rect x="178" y="101" width="40" height="5" rx="2.5" fill="#e9f2f4" />

        <circle cx="216" cy="168" r="34" fill="none" stroke="#eef6f7" strokeWidth="9" />
        <circle
          cx="216"
          cy="168"
          r="34"
          fill="none"
          stroke="url(#wiiRing)"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray="180 214"
          transform="rotate(-90 216 168)"
        />
        <text
          x="216"
          y="164"
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize="17"
          fontWeight="700"
          fill="#0c7489"
        >
          72%
        </text>
        <text
          x="216"
          y="180"
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize="7.5"
          fontWeight="600"
          fill="#7c9199"
          letterSpacing="0.05em"
        >
          TODAY&apos;S GOAL
        </text>

        <rect x="156" y="224" width="120" height="30" rx="10" fill="#f4fafb" />
        <circle cx="172" cy="239" r="5" fill="#2ec4b6" />
        <rect x="186" y="234" width="70" height="5" rx="2.5" fill="#c7dade" />
        <rect x="186" y="244" width="45" height="4" rx="2" fill="#dbe8ea" />

        <rect x="156" y="262" width="120" height="30" rx="10" fill="#f4fafb" />
        <circle cx="172" cy="277" r="5" fill="#1eaedb" />
        <rect x="186" y="272" width="70" height="5" rx="2.5" fill="#c7dade" />
        <rect x="186" y="282" width="50" height="4" rx="2" fill="#dbe8ea" />

        <rect x="156" y="300" width="120" height="30" rx="10" fill="#f4fafb" />
        <circle cx="172" cy="315" r="5" fill="#0c7489" />
        <rect x="186" y="310" width="70" height="5" rx="2.5" fill="#c7dade" />
        <rect x="186" y="320" width="38" height="4" rx="2" fill="#dbe8ea" />
      </svg>

      <div className={`${styles.illustrationBadge} ${styles.illustrationBadgeLeaf}`}>
        <Leaf size={18} aria-hidden="true" />
      </div>
      <div className={`${styles.illustrationBadge} ${styles.illustrationBadgeActivity}`}>
        <Activity size={18} aria-hidden="true" />
      </div>
      <div className={`${styles.illustrationBadge} ${styles.illustrationBadgeCalendar}`}>
        <CalendarCheck size={18} aria-hidden="true" />
      </div>
      <div className={`${styles.illustrationBadge} ${styles.illustrationBadgeChat}`}>
        <MessageCircle size={18} aria-hidden="true" />
      </div>
    </div>
  );
};

const AUDIENCE_ITEMS = [
  {
    icon: Stethoscope,
    title: 'Dietitians & nutritionists',
    text: 'Clients following a plan built by a registered dietitian or nutritionist.',
  },
  {
    icon: Activity,
    title: 'Health coaches',
    text: 'Clients working with a health coach on nutrition, habits and lifestyle goals.',
  },
  {
    icon: Users,
    title: 'Fitness professionals',
    text: 'Clients whose trainer or fitness coach manages their nutrition alongside training.',
  },
  {
    icon: CalendarCheck,
    title: 'Invited clients',
    text: "Access is invite-only: your professional connects your account before you can sign in.",
  },
];

const AudienceAccordion: React.FC = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className={styles.audienceList}>
      {AUDIENCE_ITEMS.map(({ icon: Icon, title, text }, index) => {
        const isOpen = openIndex === index;
        return (
          <article className={styles.audienceCard} key={title}>
            <button
              type="button"
              className={styles.audienceHeader}
              onClick={() => setOpenIndex(isOpen ? -1 : index)}
              aria-expanded={isOpen}
            >
              <span className={styles.audienceIcon} aria-hidden="true">
                <Icon size={20} />
              </span>
              <span className={styles.audienceTitle}>{title}</span>
              <span className={styles.faqIcon} aria-hidden="true">
                {isOpen ? <ChevronUp /> : <ChevronDown />}
              </span>
            </button>
            {isOpen ? <p className={styles.audienceText}>{text}</p> : null}
          </article>
        );
      })}
    </div>
  );
};

const FaqAccordion: React.FC = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className={styles.faqList}>
      {COMPANION_APP_FAQS.map((faq, index) => {
        const isOpen = openIndex === index;
        return (
          <article className={styles.faqItem} key={faq.question}>
            <button
              type="button"
              className={styles.faqQuestion}
              onClick={() => setOpenIndex(isOpen ? -1 : index)}
              aria-expanded={isOpen}
            >
              <span>{faq.question}</span>
              <span className={styles.faqIcon} aria-hidden="true">
                {isOpen ? <ChevronUp /> : <ChevronDown />}
              </span>
            </button>
            {isOpen ? <p>{faq.answer}</p> : null}
          </article>
        );
      })}
    </div>
  );
};

const CompanionAppPage: React.FC = () => {
  useCompanionAppSeo();

  const [heroShot, sessionsShot, trendsShot, mealDetailsShot, invoicesShot] = COMPANION_APP_SCREENSHOTS;

  return (
    <div className={styles.page}>
      <div className={styles.heroBackdrop}>
        <Navbar />
        <section className={styles.hero}>
          <div className={styles.inner}>
            <div className={styles.heroInner}>
              <div>
                <h1 className={styles.heroTitle}>
                  Your plan, your progress, <em>always in reach</em>
                </h1>
                <p className={styles.heroSubtitle}>{COMPANION_APP_META_DESCRIPTION}</p>
                <div className={styles.heroCtas}>
                  <StoreBadges id="companion_hero_store" />
                </div>
              </div>
              <div className={styles.heroVisual}>
                <img src={COMPANION_APP_BANNER_PATH} alt={`${COMPANION_APP_NAME} app preview`} />
              </div>
            </div>
          </div>
        </section>
      </div>
      <main className={styles.main}>
        {/* What is it */}
        <section className={styles.section} id="what-is-it">
          <div className={styles.inner}>
            <div className={styles.illustrationWrap}>
              <div className={styles.sectionHead}>
                <span className={styles.eyebrow}>
                  <Leaf size={13} className={styles.eyebrowIcon} aria-hidden="true" />
                  What it is
                </span>
                <h2 className={styles.sectionTitle}>
                  The client app for your <em>nutrition, health &amp; fitness</em> journey
                </h2>
                <p className={styles.sectionText}>
                  {COMPANION_APP_NAME} is the client-facing companion to the CalorieScience practice platform. Once
                  your dietitian, nutritionist, health coach or fitness professional invites you to connect,
                  everything they set up for you — meal plan, sessions, questionnaires and progress tracking — is
                  available in a single app on your phone.
                </p>
              </div>
              <WhatItIsIllustration />
            </div>
          </div>
        </section>

        {/* Who it's for */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="who-its-for">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <span className={styles.eyebrow}>
                <Leaf size={13} className={styles.eyebrowIcon} aria-hidden="true" />
                Who it's for
              </span>
              <h2 className={styles.sectionTitle}>
                Built for clients working with <em>a professional</em>
              </h2>
              <p className={styles.sectionText}>
                {COMPANION_APP_NAME} is for individuals who are actively working with a nutrition, health or fitness
                professional, not a general calorie-counting app for the public.
              </p>
            </div>
            <AudienceAccordion />
          </div>
        </section>

        {/* How clients connect */}
        <section className={styles.section} id="how-you-connect">
          <div className={styles.inner}>
            <div className={styles.cardStack}>
              <article className={styles.scaleCard} style={{ '--card-index': 0 } as React.CSSProperties}>
                <div className={styles.cardDocumentPanel}>
                  <img src={sessionsShot.src} alt={sessionsShot.alt} loading="lazy" />
                </div>
                <div className={styles.cardCopy}>
                  <h3>How clients connect with their professional</h3>
                  <p>
                    Your dietitian, nutritionist, health coach or fitness professional sends you an invitation from
                    their CalorieScience account. Accepting it links your app to their practice, so your plan,
                    sessions and messages stay in sync automatically.
                  </p>
                  <ul className={styles.checkList}>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Secure, invite-only connection to your professional
                    </li>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Book, confirm and reschedule sessions in the app
                    </li>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Review program invoices and payment status
                    </li>
                  </ul>
                  <span className={styles.cardStepNumber} aria-hidden="true">01</span>
                </div>
              </article>

              <article className={styles.scaleCard} style={{ '--card-index': 1 } as React.CSSProperties}>
                <div className={styles.cardDocumentPanel}>
                  <img src={heroShot.src} alt={heroShot.alt} loading="lazy" />
                </div>
                <div className={styles.cardCopy}>
                  <h3>Meal plan access, always up to date</h3>
                  <p>
                    Your meal plan appears in the app exactly as your professional built it, with meal timings,
                    portions and macros. Whenever they update your plan, the app updates too.
                  </p>
                  <ul className={styles.checkList}>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Daily meals with calories, protein, carbs, fats and fiber
                    </li>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Recipe steps, ingredients and photos for every meal
                    </li>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Automatic updates when your professional revises your plan
                    </li>
                  </ul>
                  <span className={styles.cardStepNumber} aria-hidden="true">02</span>
                </div>
              </article>

              <article className={styles.scaleCard} style={{ '--card-index': 2 } as React.CSSProperties}>
                <div className={styles.cardDocumentPanel}>
                  <img src={mealDetailsShot.src} alt={mealDetailsShot.alt} loading="lazy" />
                </div>
                <div className={styles.cardCopy}>
                  <h3>Track food, macros, symptoms and progress</h3>
                  <p>
                    Log meals and macros against your daily targets, note symptoms between sessions, and watch your
                    weight and biomarker trends move over time, so your professional always has the full picture.
                  </p>
                  <ul className={styles.checkList}>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Food and macro tracking against your personalized targets
                    </li>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Symptom notes your professional can review
                    </li>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Weight and goal progress charted automatically
                    </li>
                  </ul>
                  <span className={styles.cardStepNumber} aria-hidden="true">03</span>
                </div>
              </article>

              <article className={styles.scaleCard} style={{ '--card-index': 3 } as React.CSSProperties}>
                <div className={styles.cardDocumentPanel}>
                  <img src={trendsShot.src} alt={trendsShot.alt} loading="lazy" />
                </div>
                <div className={styles.cardCopy}>
                  <h3>Upload lab reports in seconds</h3>
                  <p>
                    Photograph or upload a lab report directly from your phone. Your professional can review your
                    biomarkers, like glucose, HbA1c, thyroid markers and vitamin levels, and factor them into your plan.
                  </p>
                  <ul className={styles.checkList}>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Upload reports as a photo or PDF from your device
                    </li>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Biomarkers stay attached to your progress history
                    </li>
                    <li>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      Shared only with the professional you're connected to
                    </li>
                  </ul>
                  <span className={styles.cardStepNumber} aria-hidden="true">04</span>
                </div>
              </article>

              <article className={styles.scaleCard} style={{ '--card-index': 4 } as React.CSSProperties}>
                <div className={styles.cardDocumentPanel}>
                  <img src={invoicesShot.src} alt={invoicesShot.alt} loading="lazy" />
                </div>
                <div className={styles.cardCopy}>
                  <h3>Invoices and program details, on the go</h3>
                  <p>
                    Keep track of invoices, payment status and your nutrition program details without leaving the
                    app.
                  </p>
                  <ul className={styles.checkList}>
                    <li>
                      <Receipt size={18} aria-hidden="true" style={{ color: '#2ec4b6' }} />
                      Review and download invoices from your professional
                    </li>
                    <li>
                      <FileText size={18} aria-hidden="true" style={{ color: '#2ec4b6' }} />
                      See your program, payment status and due dates
                    </li>
                  </ul>
                  <span className={styles.cardStepNumber} aria-hidden="true">05</span>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Privacy & data security */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="privacy-and-security">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <span className={styles.eyebrow}>
                <ShieldCheck size={13} className={styles.eyebrowIcon} aria-hidden="true" />
                Privacy &amp; data security
              </span>
              <h2 className={styles.sectionTitle}>
                Your health data, <em>protected</em>
              </h2>
              <p className={styles.sectionText}>
                {COMPANION_APP_NAME} only shares your data with the professional you're connected to. Nothing you
                upload is visible to anyone else.
              </p>
            </div>
            <div className={styles.privacyGrid}>
              <div className={styles.privacyCard}>
                <h3>
                  <Lock size={18} aria-hidden="true" /> Encrypted &amp; access-controlled
                </h3>
                <p>Your data and your lab reports are encrypted in transit and at rest, with access limited to your connected professional.</p>
              </div>
              <div className={styles.privacyCard}>
                <h3>
                  <ShieldCheck size={18} aria-hidden="true" /> UK &amp; EU GDPR compliant
                </h3>
                <p>CalorieScience is built to UK and EU GDPR standards. HIPAA compliance is in progress.</p>
              </div>
              <div className={styles.privacyCard}>
                <h3>
                  <Users size={18} aria-hidden="true" /> Invite-only access
                </h3>
                <p>You only appear in the app connected to the professional who invited you — there are no public profiles.</p>
              </div>
            </div>
            <p className={styles.disclosure}>{COMPANION_APP_COMPLIANCE_DISCLOSURE}</p>
          </div>
        </section>

        {/* How to download */}
        <section className={styles.section} id="how-to-download">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <span className={styles.eyebrow}>
                <Leaf size={13} className={styles.eyebrowIcon} aria-hidden="true" />
                Getting started
              </span>
              <h2 className={styles.sectionTitle}>
                How to <em>download and access</em> the app
              </h2>
            </div>
            <div className={styles.downloadGrid}>
              <div className={styles.downloadStep}>
                <span className={styles.downloadStepNumber}>1</span>
                <h3>Get invited</h3>
                <p>Your dietitian, nutritionist, health coach or fitness professional sends you an invite from CalorieScience.</p>
              </div>
              <div className={styles.downloadStep}>
                <span className={styles.downloadStepNumber}>2</span>
                <h3>Download the app</h3>
                <p>Install {COMPANION_APP_NAME} from the App Store. Android is coming soon.</p>
              </div>
              <div className={styles.downloadStep}>
                <span className={styles.downloadStepNumber}>3</span>
                <h3>Sign in &amp; connect</h3>
                <p>Sign in with your invited email to connect your account and access your plan.</p>
              </div>
            </div>
            <div className={styles.downloadCtaRow}>
              <StoreBadges id="companion_download_section_store" />
              {!COMPANION_APP_STORE_URL && !COMPANION_PLAY_STORE_URL ? (
                <p className={styles.comingSoonNote}>The App Store and Google Play listings aren't live yet, check back soon.</p>
              ) : null}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={`${styles.section} ${styles.sectionAlt}`} id="faq">
          <div className={styles.inner}>
            <div className={styles.sectionHead}>
              <span className={styles.eyebrow}>FAQ</span>
              <h2 className={styles.sectionTitle}>Frequently asked questions</h2>
            </div>
            <FaqAccordion />
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default CompanionAppPage;
